import { jwtDecode } from 'jwt-decode'
import type { AccessTokenResponse } from '../../../sdk-client/gen/models/responsesModels/AccessTokenResponse'
import { EMAIL_AUTH_STORAGE_KEYS, IAuthProvider, User, setStoredAuthProvider } from '../AuthProvider'
import { clearUserProfile } from '../profileStorage'

const isBrowser = () => typeof window !== 'undefined'

export const clearEmailAuthSession = () => {
  if (!isBrowser()) return
  window.localStorage.removeItem(EMAIL_AUTH_STORAGE_KEYS.accessToken)
  window.localStorage.removeItem(EMAIL_AUTH_STORAGE_KEYS.expiresAt)
  clearUserProfile()
  setStoredAuthProvider(null)
}

export const setEmailAuthSession = (session?: AccessTokenResponse | null) => {
  if (!isBrowser()) return
  if (!session?.accessToken) {
    clearEmailAuthSession()
    return
  }

  window.localStorage.setItem(EMAIL_AUTH_STORAGE_KEYS.accessToken, session.accessToken)
  if (session.expiresAt) {
    window.localStorage.setItem(EMAIL_AUTH_STORAGE_KEYS.expiresAt, String(session.expiresAt))
  }

  setStoredAuthProvider('useEmail')
}

const getStoredToken = (): string | null => {
  if (!isBrowser()) return null
  return window.localStorage.getItem(EMAIL_AUTH_STORAGE_KEYS.accessToken)
}

const isTokenExpired = (exp?: number) => {
  if (!exp) return false
  return Date.now() >= exp * 1000
}

export const emailAuthProvider = (): IAuthProvider => {
  return {
    login: () => {
      if (!isBrowser()) return
      // Redirect to login page (root path)
      window.location.href = '/'
    },
    logout: () => {
      clearEmailAuthSession()
    },
    getUser: async () => {
      const token = getStoredToken()
      if (!token) return

      try {
        const decoded = jwtDecode<User>(token)
        if (isTokenExpired(decoded?.exp)) {
          clearEmailAuthSession()
          return
        }

        setStoredAuthProvider('useEmail')
        return decoded
      } catch {
        clearEmailAuthSession()
        return
      }
    },
    getToken: () => {
      return getStoredToken()
    },
    getHeaders: () => {
      const token = getStoredToken()
      const headers: { [key: string]: string } = {}

      if (token) {
        headers.Authorization = `Bearer ${token}`
      }

      return headers
    }
  }
}
